import { call, put, takeEvery } from "redux-saga/effects";
import { get } from "lodash";

import {
  fetchClaimPaymentRetry,
  setClaimPaymentRetry,
  setClaimPaymentRetryQueue,
  setLoading,
} from "../actions";
import axios from "../utilities/axiosWrapper";
import { Auth } from "../utilities/auth";

/**
 * The bank refuses a claim payment when the bank account of the member is
 * wrong. An operator corrects the account and asks to send the payment again,
 * and a second operator approves that request.
 *
 * The service refuses an approval by the operator that asked, and it refuses a
 * request when the account is still the one the bank rejected. Those answers
 * arrive as errors, so the workers below pass the message of the service
 * through to the screen rather than replacing it with a generic one. The
 * operator needs to read which control stopped them.
 */

// The services answer with the deprecated envelope: fnStatusCode, fnError and
// fnResult. A refusal by one of the retry controls arrives here, and its
// message names the control, so it is passed to the screen unchanged.
const readServiceError = (jsonResponse: any): string | null => {
  const fnStatusCode = get(jsonResponse, "data.fnStatusCode");
  if (fnStatusCode && fnStatusCode !== 200) {
    return get(jsonResponse, "data.fnError") || "The request was refused";
  }
  return null;
};

const parseResult = (jsonResponse: any): any => {
  // StringResultResponse serialises Result as "fnResult". An empty string
  // means that nothing is open, which is a normal state.
  const result = get(jsonResponse, "data.fnResult");
  if (!result) {
    return null;
  }
  try {
    return JSON.parse(result);
  } catch (err) {
    return null;
  }
};

function* fetchClaimPaymentRetryWorker(action: any): Generator {
  try {
    const apiKeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(
      axios,
      { method: "GET", path: `claims/${action.data}/payment/retry` },
      apiKeys
    );

    const errorMessage = readServiceError(jsonResponse);
    if (errorMessage) {
      throw { message: errorMessage };
    }

    // An empty result means that no request waits for a decision. That is a
    // normal state and not an error.
    return yield put(setClaimPaymentRetry(parseResult(jsonResponse)));
  } catch (err) {
    // This read runs when the claim opens, so a failure must not raise an
    // alert over the work the operator came to do. The panel simply shows no
    // open request, and the write endpoints still report their own errors.
    console.error("failed to load the claim payment retry", err);
    return yield put(setClaimPaymentRetry(null));
  }
}

function* fetchClaimPaymentRetryQueueWorker(): Generator {
  try {
    const apiKeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(
      axios,
      { method: "GET", path: "claims/payment/retries?status=pending_approval" },
      apiKeys
    );

    const errorMessage = readServiceError(jsonResponse);
    if (errorMessage) {
      throw { message: errorMessage };
    }

    return yield put(setClaimPaymentRetryQueue(parseResult(jsonResponse) || []));
  } catch (err) {
    // The queue loads with the adjudication inbox, for every operator, whether
    // or not they use this feature. A failure hides the section rather than
    // interrupting them.
    console.error("failed to load the claim payment retry queue", err);
    return yield put(setClaimPaymentRetryQueue([]));
  }
}

function* requestClaimPaymentRetryWorker(action: any): Generator {
  yield put(setLoading({ loading: true }));
  try {
    const apiKeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, action.data.request, apiKeys);

    const errorMessage = readServiceError(jsonResponse);
    if (errorMessage) {
      throw { message: errorMessage };
    }

    yield put(
      setLoading({
        loading: false,
        alertType: "success",
        successMessage:
          "Payment retry requested. A second operator must approve it before the payment goes out.",
      })
    );
    return yield put(fetchClaimPaymentRetry(action.data.claimGuid));
  } catch (err) {
    return yield put(setLoading({ loading: false, errorMessage: err.message }));
  }
}

function* reviewClaimPaymentRetryWorker(action: any): Generator {
  yield put(setLoading({ loading: true }));
  try {
    const apiKeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(axios, action.data.request, apiKeys);

    const errorMessage = readServiceError(jsonResponse);
    if (errorMessage) {
      throw { message: errorMessage };
    }

    yield put(
      setLoading({
        loading: false,
        alertType: "success",
        successMessage:
          action.data.decision === "approve"
            ? "Payment retry approved. The payment goes out shortly."
            : "Payment retry refused.",
      })
    );
    return yield put(fetchClaimPaymentRetry(action.data.claimGuid));
  } catch (err) {
    return yield put(setLoading({ loading: false, errorMessage: err.message }));
  }
}

export function* fetchClaimPaymentRetryWatcher(): Generator {
  yield takeEvery("FETCH_CLAIM_PAYMENT_RETRY", fetchClaimPaymentRetryWorker);
}

export function* fetchClaimPaymentRetryQueueWatcher(): Generator {
  yield takeEvery(
    "FETCH_CLAIM_PAYMENT_RETRY_QUEUE",
    fetchClaimPaymentRetryQueueWorker
  );
}

export function* requestClaimPaymentRetryWatcher(): Generator {
  yield takeEvery(
    "REQUEST_CLAIM_PAYMENT_RETRY",
    requestClaimPaymentRetryWorker
  );
}

export function* reviewClaimPaymentRetryWatcher(): Generator {
  yield takeEvery("REVIEW_CLAIM_PAYMENT_RETRY", reviewClaimPaymentRetryWorker);
}
